
import Vue from "vue";
import Component from "vue-class-component";
import { namespace } from "vuex-class";
import { Prop } from "vue-property-decorator";
import { IUser } from "@/model/User/UserInterface";
import { EPermissionActions } from "@/model/EPermissionActions";
import LockerSearch from "@/components/locker/widget/LockerSearch.vue";
import {IOnOffHandlers} from "bonomo/dist";
import {IShortLocker} from "@/model/shortInterfaces";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import {IAuthUserData} from "@/store/modules/authModule";
import {IErrors} from "@/model/IErrors";
import {flattenErrors} from "@/helpers/errorsHelper";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";

const lockerModule = namespace("lockerModule");
const userModule = namespace("userModule");
const authModule = namespace("authModule");


@Component({
  components: {LockerChip, LockerSearch },
})
export default class UserLockerMigration extends Vue {
  @lockerModule.Getter("lockers")
  private lockers!: any[];
  @lockerModule.Action("fetchLockers")
  private fetchLockers!: () => void;

  @userModule.Action("migrate")
  private migrate!: (data: { lockerId: number, userId: number }) => void;

  @authModule.Getter("userData")
  private authUser!: IAuthUserData;

  @Prop()
  user!: IUser;

  selectedLocker: number | null = null;
  editing: boolean = false;
  loading: boolean = false;
  submitting: boolean = false;
  subscribes: IOnOffHandlers = {};

  get isSelf(): boolean {
    return '' + this.user.id === '' + this.authUser.id;
  }

  get isDeveloper(): boolean {
    return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_DEVELOPER, 0);
  }

  // developers and the user moving themselves are not blocked here; the backend decides with its errors
  get canEdit(): boolean {
    if (this.user.is_from_integration && !this.isSelf && !this.isDeveloper) {
      return false;
    }

    return this.$canAction(EPermissionActions.ACTION_USERS_UPDATE)
      || (this.isSelf && this.$canAction(EPermissionActions.ACTION_USERS_ALLOW_EDIT_HIMSELF));
  }

  get saveEnabled(): boolean {
    return (this.selectedLocker !== null) && this.selectedLocker != this.user.locker_id;
  }

  get usersLocker(): IShortLocker {
    let locker: IShortLocker|null = this.lockers ? this.lockers.find(each => each.id == this.user.locker_id) : null;
    const ret = (locker ? locker : { id: 0, name: '...' }) as IShortLocker;
    return ret;
  }

  created() {
    this.selectedLocker = this.user.locker_id || null;
    if (!this.lockers || !this.lockers.length) {
      this.loading = true;
      this.fetchLockers();
    }
    this.subscribes = this.$ons({
      'backend.call.OK.lockersLoaded': () => this.loading = false,
      'backend.call.OK.userMigrated': this.onMigrated,
      'backend.call.KO.userMigrated': this.onMigrateFailed,
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  onCancel() {
    this.editing = false;
    this.selectedLocker = this.user.locker_id || null;
  }

  onEdit() {
    this.editing = true;
  }

  onSave() {
    if (this.selectedLocker !== null) {
      this.migrate({ lockerId: this.selectedLocker, userId: this.user.id});
      this.submitting = true;
    }
  }

  onLockerSelected(newLockerId: number) {
    if (this.editing) {
      this.selectedLocker = newLockerId;
    }
  }

  onMigrated() {
    location.reload();
  }

  // userMigrated is silenced in errorHandling, so the translated backend error is shown here
  onMigrateFailed(errors: IErrors) {
    this.submitting = false;
    this.$uiBus.emit('alert', flattenErrors(errors || {}).join(' / ') || 'KO');
  }

}
